import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, TrendingUp, BookOpen, Gamepad2, PenTool,
  Trophy, Flame, Clock, Target, Star, ArrowUp, ArrowDown,
  CheckCircle, AlertCircle, ChevronLeft, Calendar
} from 'lucide-react';

const StudentAnalytics = () => {
  const { user, api } = useAuth();
  const [overview, setOverview] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [strengths, setStrengths] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, chartRes, subjectsRes, strengthsRes] = await Promise.all([
        api.get('/v2/analytics/student/overview'),
        api.get(`/v2/analytics/student/progress-chart?days=${selectedPeriod}`),
        api.get('/v2/analytics/student/subject-breakdown'),
        api.get('/v2/analytics/student/strengths-weaknesses')
      ]);
      
      setOverview(overviewRes.data);
      setChartData(chartRes.data);
      setSubjects(subjectsRes.data);
      setStrengths(strengthsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Simple line chart visualization
  const MiniChart = ({ data, valueKey, color = 'emerald' }) => {
    if (!data || data.length === 0) return null;
    
    const values = data.map(d => d[valueKey] || 0);
    const max = Math.max(...values) || 1;
    const points = values.map((v, i) => {
      const x = (i / (values.length - 1)) * 100;
      const y = 100 - (v / max) * 100;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="h-20 relative">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
          <polyline
            points={points}
            fill="none"
            stroke={color === 'emerald' ? '#10B981' : color === 'purple' ? '#8B5CF6' : '#F59E0B'}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={`0,100 ${points} 100,100`}
            fill={color === 'emerald' ? '#10B98120' : color === 'purple' ? '#8B5CF620' : '#F59E0B20'}
          />
        </svg>
      </div>
    );
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </StudentLayout>
    );
  }

  const colorMap = {
    maths: 'bg-amber-500',
    english: 'bg-rose-500',
    science: 'bg-emerald-500',
    esol: 'bg-blue-500',
    '11plus': 'bg-purple-500'
  };

  return (
    <StudentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/student" className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-2">
              <ChevronLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-slate-800">My Analytics</h1>
            <p className="text-slate-500">Track your learning progress</p>
          </div>
          <div className="flex gap-2">
            {[7, 14, 30].map(days => (
              <Button
                key={days}
                variant={selectedPeriod === days ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod(days)}
                data-testid={`period-${days}`}
              >
                {days} days
              </Button>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="pt-6">
              <BookOpen className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{overview?.totals?.lessons_completed || 0}</p>
              <p className="text-sm opacity-80">Lessons Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardContent className="pt-6">
              <Gamepad2 className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{overview?.totals?.games_played || 0}</p>
              <p className="text-sm opacity-80">Games Played</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <CardContent className="pt-6">
              <Star className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{overview?.totals?.total_points || 0}</p>
              <p className="text-sm opacity-80">Total Points</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="pt-6">
              <Flame className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{overview?.totals?.streak_days || 0}</p>
              <p className="text-sm opacity-80">Day Streak</p>
            </CardContent>
          </Card>
        </div>

        {/* Average Scores */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Lesson Avg</span>
                <span className="text-2xl font-bold text-emerald-600">{overview?.averages?.lesson_score || 0}%</span>
              </div>
              <Progress value={overview?.averages?.lesson_score || 0} className="h-2" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Game Avg</span>
                <span className="text-2xl font-bold text-purple-600">{overview?.averages?.game_score || 0}</span>
              </div>
              <Progress value={Math.min(overview?.averages?.game_score || 0, 100)} className="h-2" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Essay Avg</span>
                <span className="text-2xl font-bold text-amber-600">{overview?.averages?.essay_score || 0}%</span>
              </div>
              <Progress value={overview?.averages?.essay_score || 0} className="h-2" />
            </CardContent>
          </Card>
        </div>

        {/* Progress Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Learning Progress (Last {selectedPeriod} Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-slate-500 mb-2">Lessons per Day</p>
                <MiniChart data={chartData?.chart_data} valueKey="lessons" color="emerald" />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>{chartData?.chart_data?.[0]?.date?.slice(5)}</span>
                  <span>Today</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">Points Earned</p>
                <MiniChart data={chartData?.chart_data} valueKey="points" color="purple" />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>{chartData?.chart_data?.[0]?.date?.slice(5)}</span>
                  <span>Today</span>
                </div>
              </div>
            </div>
            
            {/* Cumulative Stats */}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-600">
                  {chartData?.chart_data?.[chartData.chart_data.length - 1]?.cumulative_lessons || 0}
                </p>
                <p className="text-sm text-slate-500">Total Lessons</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">
                  {chartData?.chart_data?.[chartData.chart_data.length - 1]?.cumulative_points || 0}
                </p>
                <p className="text-sm text-slate-500">Total Points</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subject Breakdown & Strengths */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Subject Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Subject Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {subjects?.subjects?.map(subject => (
                  <div key={subject.subject} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colorMap[subject.subject] || 'bg-slate-400'}`}></div>
                        <span className="font-medium capitalize">{subject.subject}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span>{subject.lessons_completed} lessons</span>
                        <span className="font-semibold text-slate-700">{subject.average_score}%</span>
                      </div>
                    </div>
                    <Progress value={subject.average_score} className="h-2" />
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>{subject.topics_covered} topics covered</span>
                      <span>{subject.time_spent_minutes} min spent</span>
                    </div>
                  </div>
                ))}
                {(!subjects?.subjects || subjects.subjects.length === 0) && (
                  <p className="text-center text-slate-400 py-4">No subject data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Strengths & Weaknesses */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-500" />
                Strengths & Areas to Improve
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Strengths */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-emerald-600 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Your Strengths
                </h4>
                <div className="space-y-2">
                  {strengths?.strengths?.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
                      <div>
                        <span className="font-medium text-slate-700">{item.topic}</span>
                        <span className="text-xs text-slate-400 ml-2">({item.subject})</span>
                      </div>
                      <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300">
                        {item.average_score}%
                      </Badge>
                    </div>
                  ))}
                  {(!strengths?.strengths || strengths.strengths.length === 0) && (
                    <p className="text-slate-400 text-sm">Complete more lessons to discover your strengths!</p>
                  )}
                </div>
              </div>

              {/* Weaknesses */}
              <div>
                <h4 className="text-sm font-semibold text-amber-600 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Areas to Improve
                </h4>
                <div className="space-y-2">
                  {strengths?.weaknesses?.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2">
                      <div>
                        <span className="font-medium text-slate-700">{item.topic}</span>
                        <span className="text-xs text-slate-400 ml-2">({item.subject})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.improvement > 0 && (
                          <span className="text-xs text-emerald-600 flex items-center">
                            <ArrowUp className="w-3 h-3" />
                            {item.improvement}%
                          </span>
                        )}
                        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                          {item.average_score}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {(!strengths?.weaknesses || strengths.weaknesses.length === 0) && (
                    <p className="text-slate-400 text-sm">Great job! No weak areas identified.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* This Week Summary */}
        <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-6 h-6" />
              <h3 className="text-lg font-bold">This Week's Activity</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{overview?.this_week?.lessons || 0}</p>
                <p className="text-sm opacity-80">Lessons</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{overview?.this_week?.games || 0}</p>
                <p className="text-sm opacity-80">Games</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{overview?.totals?.achievements_earned || 0}</p>
                <p className="text-sm opacity-80">Badges</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{overview?.total_time_minutes || 0}</p>
                <p className="text-sm opacity-80">Minutes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </StudentLayout>
  );
};

export default StudentAnalytics;
